import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex, Stack, useColorModeValue } from "@chakra-ui/react";
import Loading from "components/Loading/Loading";
import { ManagerialFilter } from "./ManagerialFilter";
import { ManagerialCard } from "./ManagerialCard";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { getStatusLabel, getStatusColor, groupByStatus } from "utils/reports-utils";
import { CREATED, ACCEPTED, REJECTED, STATUS_MAP, PROCESS_TYPES } from "constants/reports";
import { reportsService } from "services/reportsService";

function ManagerialList() {
    const { t } = useTranslation();
    const showToast = useCustomToast();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({
        traffic_violations: [],
        processes: [],
        x_axis_labels: [],
    });
    const cardBg = useColorModeValue("#FFFFFF", "#1f2733");
    const textColor = useColorModeValue("#1f2733", "#FFFFFF");
    const cardSpacing = "20px";

    const plotLayout = useMemo(() => ({
        autosize: true,
        responsive: true,
        xaxis: { type: "category", color: textColor },
        yaxis: { color: textColor },
        legend: {
            orientation: "h",
            x: 0.5,
            y: -0.3,
            xanchor: "center",
            font: { color: textColor },
        },
        plot_bgcolor: cardBg,
        paper_bgcolor: cardBg,
        font: { color: textColor },
    }), [textColor, cardBg]);

    const fetchData = async (filters = {}) => {
        setLoading(true);
        try {
            const res = await reportsService.managerial(filters);
            setData({
                traffic_violations: res.traffic_violations || [],
                processes: res.processes || [],
                x_axis_labels: res.x_axis_labels || []
            });
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        } finally {
            setLoading(false);
        }
    };

    const getLineChartData = (field) => {
        if (!data?.traffic_violations) return [];

        const isValid = field === "count";
        const label = isValid
            ? t("reports.managerial.violations.valid-violations")
            : t("reports.managerial.violations.paid-violations");

        const x = data.traffic_violations.map((d) => d.interval);
        const y = data.traffic_violations.map((d) => {
            const value = d[field];
            return value === null ? null : parseFloat(value);
        });

        const maxIndex = y.reduce(
            (maxI, val, i, arr) => (val !== null && val > arr[maxI] ? i : maxI),
            0
        );

        return [
            {
                x,
                y,
                type: "scatter",
                mode: "lines+markers",
                fill: "tozeroy",
                fillcolor: isValid
                    ? "rgba(0, 123, 255, 0.25)"
                    : "rgba(40, 167, 69, 0.25)",
                line: {
                    color: isValid
                        ? "rgba(0, 123, 255, 1)"
                        : "rgba(40, 167, 69, 1)",
                    width: 2,
                },
                marker: {
                    size: y.map((_, i) => (i === maxIndex ? 10 : 8)),
                    color: isValid
                        ? "rgba(0, 123, 255, 1)"
                        : "rgba(40, 167, 69, 1)",
                },
                hovertemplate: isValid
                    ? t("reports.managerial.charts.line.valid-violations", {
                        value: "%{y}"
                    }) + "<extra></extra>"
                    : t("reports.managerial.charts.line.paid-violations", {
                        value: "%{y}"
                    }) + "<extra></extra>",
                name: label,
                showlegend: true,
            },
        ];
    };

    const getProcessesChartPieData = (type) => {
        if (!data?.processes?.length) return [];

        const filtered = data.processes.filter((p) => p.tipo === type);
        const grouped = groupByStatus(filtered);

        return [
            {
                values: [grouped[CREATED], grouped[ACCEPTED], grouped[REJECTED]],
                labels: [getStatusLabel(t, CREATED), getStatusLabel(t, ACCEPTED), getStatusLabel(t, REJECTED)],
                type: "pie",
                marker: {
                    colors: [getStatusColor(CREATED), getStatusColor(ACCEPTED), getStatusColor(REJECTED)],
                },
                hovertemplate: t("reports.managerial.charts.pie") + "<extra></extra>",
            },
        ];
    };

    const getProcessesChartLineData = (type) => {
        if (!data?.processes?.length) return [];

        const filtered = data.processes.filter((p) => p.tipo === type);

        const groupedByStatus = {
            [CREATED]: {},
            [ACCEPTED]: {},
            [REJECTED]: {},
        };

        filtered.forEach((p) => {
            const status = STATUS_MAP[p.status] || p.status;
            const interval = parseInt(p.interval, 10);
            groupedByStatus[status][interval] = (groupedByStatus[status][interval] || 0) + p.count;
        });

        const intervals = Array.from(new Set(filtered.map((p) => parseInt(p.interval, 10)))).sort((a, b) => a - b);

        return [CREATED, ACCEPTED, REJECTED].map((status) => ({
            x: intervals,
            y: intervals.map((interval) => groupedByStatus[status][interval] || 0),
            type: "scatter",
            mode: "lines+markers",
            name: getStatusLabel(t, status),
            fill: "tozeroy",
            line: { color: getStatusColor(status), width: 2 },
            marker: { size: 10 },
            hovertemplate: t("reports.managerial.charts.line.processes", { value: "%{y}" }) + "<extra></extra>",
        }));
    };

    return (
        <Flex direction="column" gap="5" pt={{ base: "150px", lg: "90px" }}>
            <Flex
                w="100%"
                px="30px"
                justify="flex-end"
                align="center"
                direction={{ sm: "column", md: "row" }}
            >
                <ManagerialFilter onFilter={(filters) => { fetchData(filters) }} />
            </Flex>

            {loading ? (
                <Loading maxH={{ sm: "100px", md: "350px" }} />
            ) : (
                <>
                    <Stack w="100%" direction={{ base: "column", md: "row" }} spacing={cardSpacing} flexWrap="wrap">
                        <ManagerialCard
                            title={t("reports.managerial.violations.title")}
                            charts={[
                                { data: getLineChartData("count"), layout: plotLayout },
                                { data: getLineChartData("profit"), layout: plotLayout },
                            ]}
                        />
                    </Stack>

                    {Object.keys(PROCESS_TYPES).map((typeKey) => (
                        <Stack key={typeKey} w="100%" direction={{ base: "column", md: "row" }} spacing={cardSpacing} flexWrap="wrap">
                            <ManagerialCard
                                title={t(`reports.managerial.process-${typeKey}`)}
                                charts={[
                                    { data: getProcessesChartPieData(typeKey.toUpperCase()), layout: plotLayout },
                                    { data: getProcessesChartLineData(typeKey.toUpperCase()), layout: plotLayout },
                                ]}
                            />
                        </Stack>
                    ))}
                </>
            )}
        </Flex>
    );
};

export default ManagerialList;